import type { JSX } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConsentGate } from '@/shared/components/ConsentGate';
import { useRefereeVerificationInfo } from '../hooks/useRefereeVerificationInfo';
import { refereeAppService } from '../services/referee-app.service';
import { RefereeAppLayout } from './RefereeAppLayout';

export function AnonymousRefereeWrapper(): JSX.Element {
  const { shareId } = useParams<{ shareId: string }>();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useRefereeVerificationInfo(shareId ?? '');

  const handleConsent = useCallback(async (): Promise<void> => {
    try {
      await refereeAppService.giveConsent(shareId ?? '');
      await queryClient.invalidateQueries({ queryKey: ['referee-verification', shareId] });
    } catch {
      toast.error('Failed to record consent. Please try again.');
    }
  }, [shareId, queryClient]);

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (isError) {
    const errorMessage = error?.message?.includes('410')
      ? 'This share link has expired. Please contact the company for a new link.'
      : 'Reference contact link not found.';
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Link Error</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (data && !data.refereeConsentGiven) {
    return <ConsentGate onConsent={() => void handleConsent()} />;
  }

  return <RefereeAppLayout />;
}
